import { createForm } from '@seek/forms-ui';
import {
  createValidator,
  fileExtension,
  maxFileSize,
  noEmptyFile,
  requiredFile,
  runValidation,
} from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { Box, Stack, Text, useToast } from 'braid-design-system';
import { useState } from 'react';
import {
  type DropEvent,
  type FileRejection,
  useDropzone,
} from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';

import { ResponseErrorReason } from 'src/App/AppPageBlock/graphql/hirer-types.generated';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import type { PageConfig } from 'src/libs/PageConfig';

import translations from '../../.vocab';

import { DropZoneContent } from './DropZoneContent';
import {
  useProcessUploadedSupportDocMutation,
  useUploadPresignedUrlForCandidateSupportDocLazyQuery,
} from './UploadAttachment.hirer.generated';

import * as styles from './AddAttachment.css';

interface Props {
  onAddAttachmentClick: () => void;
  onAddAttachmentSuccess: () => void;
  applicationCorrelationId: string;
  jobId: string;
  candidateSupportDocCount: number;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
}

const fieldName = 'attachment';

interface FormValues {
  attachment?: File;
}

const { FormProvider, useField } = createForm<FormValues>('file-upload-form', {
  formType: 'braid',
});

// https://github.com/SEEK-Jobs/validators-js/blob/0ac6bcf3d3f8c59373180ace9fa89780ad18e4be/src/validators/file/fileName/fileName.ts
// added support for space unicode character in Mac OS Screenshots, see https://github.com/rspamd/rspamd/discussions/4893
const regexLegacyBrowsers = /^([0-9A-Za-z]|[^\\\^\%\`\"\#\~\|])+$/g;
const regex = /^(\p{L}|\p{M}|\p{N}|\p{S}|[!_.*'()&$=@;:+,?%# \u{202F}|[-])+$/u;
const { fileName } = createValidator({
  test: ({ value }: { value?: File }) => {
    if (!value) {
      return true;
    }
    try {
      return regex.exec(value.name) !== null;
    } catch {
      return value.name.match(regexLegacyBrowsers) !== null;
    }
  },
  formatErrorMessages: (messages) => () =>
    messages[
      'Filenames can\'t contain special characters like " \\ /. Try renaming your file.'
    ].format(),
  translations,
  validatorName: 'fileName',
});

const AddAttachmentComponent = ({
  onAddAttachmentSuccess,
  onAddAttachmentClick,
  applicationCorrelationId,
  jobId,
  candidateSupportDocCount,
  addCustomError,
}: Props) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<
    'INVALID' | 'UNEXPECTED' | undefined
  >();

  const { t } = useTranslations(translations);
  const showToast = useToast();

  const [fetchUploadPresignedUrl, { error: fetchUploadPresignedUrlError }] =
    useUploadPresignedUrlForCandidateSupportDocLazyQuery();

  const [linkCandidateSupportDoc, { error: linkCandidateSupportDocError }] =
    useProcessUploadedSupportDocMutation();

  const candidateSupportDocCountExceeded = candidateSupportDocCount >= 20;

  const validators = [
    requiredFile,
    maxFileSize,
    noEmptyFile,
    fileName,
    fileExtension,
  ];

  const validatorProps = {
    // https://github.com/SEEK-Jobs/cm-packages/blob/f9438186ac6db991aa49fb4d25de2360f879273c/packages/hirer-job-application-service-sdk/src/generate-candidate-support-doc-upload-presigned-url.ts#L74
    maxFileSize: 4194304,
    // https://github.com/SEEK-Jobs/cm-packages/blob/f9438186ac6db991aa49fb4d25de2360f879273c/packages/hirer-job-application-service-sdk/src/application-attachment-types.ts#L130-L144
    allowedExtensions: [
      '.pdf',
      '.doc',
      '.docx',
      '.rtf',
      '.txt',
      '.jpg',
      '.jpeg',
      '.gif',
      '.png',
      '.bmp',
      '.msg',
      '.html',
    ],
  };

  const {
    onChange: onFileChange,
    errorMessage: validationErrorMessage,
    onBlur,
  } = useField({
    id: fieldName,
    validators,
    validatorProps,
  });

  const onDropAccepted = async (files: File[]) => {
    setUploadError(undefined);

    const file = files[0];
    onFileChange(file);
    onBlur();

    const { valid } = runValidation({
      fields: {
        [fieldName]: file,
      },
      rules: {
        [fieldName]: {
          validators,
          validatorProps,
        },
      },
    });

    if (!valid) {
      return;
    }

    const attachmentId = uuidv4();
    try {
      setUploading(true);
      // get presigned url
      const { data: fetchUploadPresignedUrlResponse } =
        await fetchUploadPresignedUrl({
          variables: {
            input: {
              applicationCorrelationId,
              jobId,
              attachmentId,
              fileName: file.name,
              contentLength: file.size,
              contentType: file.type,
            },
          },
          fetchPolicy: 'no-cache',
        });

      if (!fetchUploadPresignedUrlResponse) {
        return;
      }

      if (
        fetchUploadPresignedUrlResponse.uploadPresignedUrlForCandidateSupportDoc
          .__typename ===
        'UploadPresignedUrlForCandidateSupportDocResponseSuccess'
      ) {
        const {
          result: { link },
        } =
          fetchUploadPresignedUrlResponse.uploadPresignedUrlForCandidateSupportDoc;

        // upload attachment
        const uploadResponse = await fetch(link, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type,
          },
          body: file,
        });

        if (uploadResponse.ok) {
          // link attachment
          const { data: linkCandidateSupportDocResponse } =
            await linkCandidateSupportDoc({
              variables: {
                input: {
                  applicationCorrelationId,
                  jobId,
                  attachmentId,
                },
              },
            });

          if (!linkCandidateSupportDocResponse) {
            return;
          }

          if (
            linkCandidateSupportDocResponse.processUploadedSupportDoc
              .__typename === 'ProcessUploadedSupportDocResponseSuccess'
          ) {
            showToast({
              tone: 'positive',
              message: t('Attachment added'),
              closeLabel: t('Close'),
            });
            onAddAttachmentSuccess();
          }

          // handle link attachment responseError
          if (
            linkCandidateSupportDocResponse.processUploadedSupportDoc
              .__typename === 'ResponseError'
          ) {
            setUploadError('UNEXPECTED');
          }
        } else {
          throw new Error(await uploadResponse.text());
        }
      }

      // handle upload presigned url responseError
      if (
        fetchUploadPresignedUrlResponse.uploadPresignedUrlForCandidateSupportDoc
          .__typename === 'ResponseError'
      ) {
        if (
          fetchUploadPresignedUrlResponse
            .uploadPresignedUrlForCandidateSupportDoc.error ===
          ResponseErrorReason.BAD_INPUT
        ) {
          setUploadError('INVALID');
        } else {
          setUploadError('UNEXPECTED');
        }
      }
    } catch (error) {
      // handle aws s3 upload error and other unexpected errors
      addCustomError({
        error,
        context: {
          activity: 'UPLOAD_ATTACHMENT_TO_S3',
          description: 'Error when attempting: AddAttachmentComponent',
        },
      });
      setUploadError('UNEXPECTED');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
    event: DropEvent,
  ) => {
    if (event.type === 'drop') {
      onAddAttachmentClick();
    }
  };

  const onAddAttachmentButtonClick = () => {
    onAddAttachmentClick();
    open();
  };

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: false,
    onDrop,
    onDropAccepted,
    disabled: uploading || candidateSupportDocCountExceeded,
  });

  const dropzoneInputProps = getInputProps();

  const getErrorMessage = () => {
    if (validationErrorMessage) {
      return validationErrorMessage;
    }

    if (uploadError === 'INVALID') {
      return t(
        'Please make sure your document is a valid file type. Accepted file types: .jpeg, .png, .pdf, .gif, .bmp, .msg, .txt, .doc, .docx, .html, .rtf upto 4 MB.',
      );
    }

    if (
      fetchUploadPresignedUrlError ||
      linkCandidateSupportDocError ||
      uploadError === 'UNEXPECTED'
    ) {
      return t('The upload couldn’t be completed. Please try again later.');
    }

    return undefined;
  };

  return (
    <Stack space="small">
      <Box
        {...getRootProps()}
        className={{
          [styles.dropzone]: true,
          [styles.dropActive]: isDragActive,
        }}
        data-testid="add-attachment-dropzone"
      >
        <DropZoneContent
          candidateSupportDocCountExceeded={candidateSupportDocCountExceeded}
          dropzoneInputProps={dropzoneInputProps}
          errorMessage={getErrorMessage()}
          onAddAttachmentButtonClick={onAddAttachmentButtonClick}
          uploading={uploading}
        />
      </Box>
      <Text tone="secondary" size="xsmall" align="left">
        {t(
          'Keep your information secure: Avoid uploading sensitive documents such as passports or health records. Uploaded sensitive documents may be deleted.',
        )}
      </Text>
    </Stack>
  );
};

export const AddAttachment = (props: Props) => {
  const { language } = useAppConfig();

  const initialValues = {
    [fieldName]: undefined,
  };

  return (
    <FormProvider initialValues={initialValues} language={language}>
      {() => <AddAttachmentComponent {...props} />}
    </FormProvider>
  );
};
